<template>
  <div>
    <validation-observer ref="simpleRules">
      <b-form>

        <!-- Icon -->
        <b-form-group
          label="Icon"
          label-for="mc-icon"
        >
          <b-card
            class="icon-card cursor-pointer text-center mb-2 mx-50"
            @click="selectIcon"
          >

            <div class="icon-wrapper">
              <b-avatar
                size="32"
                :variant="item.variant"
              >
                <feather-icon
                  :icon="item.icon"
                  size="24"
                />
              </b-avatar>
            </div>
            <b-card-text class="icon-name text-truncate mb-0 mt-1">
              {{ item.icon }}
            </b-card-text>
          </b-card>

        </b-form-group>

        <icon-selector
          @selected="setIcon"
        />

        <!-- Variant -->
        <b-form-group
          label="Color"
          label-for="mc-variant"
        >
          <validation-provider
            #default="{ errors }"
            name="Color"
            rules="required"
          >
            <v-select
              id="variant"
              v-model="item.variant"
              :options="optionVariant"
              placeholder="Select color..."
              :state="errors.length > 0 ? false:null"
            />
            <small class="text-danger">{{ $t(errors[0]) }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Title -->
        <b-form-group
          label="Title"
          label-for="mc-title"
        >
          <validation-provider
            #default="{ errors }"
            name="Title"
            rules="required"
          >
            <b-form-input
              v-model="item.title"
              :state="errors.length > 0 ? false:null"
              placeholder="Title..."
            />
            <small class="text-danger">{{ $t(errors[0]) }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Body -->
        <b-form-group
          label="Description"
          label-for="mc-body"
        >
          <b-form-textarea
            v-model="item.body"
            placeholder="Description..."
          />

        </b-form-group>

        <!-- User Groups -->
        <b-form-group
          label="User Groups"
          label-for="mc-user-groups"
        >
          <v-select
            id="vue-select-user-groups"
            v-model="item.userGroups"
            :options="optionUserGroup"
            :reduce="group => group.id"
            label="groupName"
            placeholder="Select user groups..."
            multiple
          />
        </b-form-group>

      </b-form>
    </validation-observer>

    <div v-if="item.id">
      <h5>Files</h5>
      <form-library-file
        :library-id="item.id"
        @submit="fileUploaded"
      />
      <div
        v-for="(file,index) in item.files"
        :key="index"
        class="p-1"
      >
        <b-button
          variant="danger"
          size="sm"
          class="mr-1"
          @click="deleteFile(file)"
        >
          <feather-icon
            icon="Trash2Icon"
          />
        </b-button>
        <b-link @click="downloadFile(file)">
          {{ file }}
        </b-link>
      </div>
    </div>
  </div>

</template>

<script>
import useJwt from '@/auth/jwt/useJwt'

import {
  BFormGroup, BFormInput, BForm, BFormTextarea, BCard, BCardText, BAvatar, BLink, BButton,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import IconSelector from '@/layouts/components/IconSelector.vue'

import FormLibraryFile from './FormLibraryFile.vue'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BFormGroup,
    BFormInput,
    BForm,
    BFormTextarea,
    vSelect,
    IconSelector,
    BCard,
    BCardText,
    BAvatar,
    FormLibraryFile,
    BLink,
    BButton,
  },
  directives: {
    Ripple,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      default_item: {
        title: null,
        body: null,
        userGroups: [],
        icon: 'InfoIcon',
        variant: 'info',
      },
      required,
      initialUserGroups: [],
      optionUserGroup: [],
      optionVariant: ['primary', 'secondary', 'success', 'warning', 'danger', 'info', 'light-primary', 'light-secondary', 'light-success', 'light-warning', 'light-danger', 'light-info'],
    }
  },
  computed: {
    item: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        if (this.value) {
          return this.value
        }

        return this.default_item
      },
    },
  },
  async created() {
    if (this.editItem) {
      this.item = JSON.parse(JSON.stringify(this.editItem))
    }
    this.getUserGroupOptions()
  },
  methods: {
    openFile(response, filename) {
      const type = response.headers['content-type']
      const blob = new Blob([response.data], {
        type,
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      link.remove()
    },
    downloadFile(file) {
      useJwt.axiosIns.get(`library/download/${file}`, { responseType: 'arraybuffer' })
        .then(response => {
          if (response.data) {
            this.openFile(response, file)
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    fileUploaded(file) {
      this.item.files.push(file)
    },
    deleteFile(file) {
      this.$swal({
        title: 'Are you sure?',
        text: 'You are about to delete a file...',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          useJwt.axiosIns.delete(`library/file/${file}`)
            .then(response => {
              if (response.data) {
                this.item.files = this.item.files.filter(item => item !== file)
                this.$root.$emit('refresh_library')
              }
            })
            .catch(e => {
              console.log(e)
            })
        }
      })
    },
    getUserGroupOptions() {
      useJwt.axiosIns.get('usergroup').then(response => {
        this.optionUserGroup = response.data
      })
    },
    selectIcon() {
      this.$bvModal.show('iconSelector')
    },
    setIcon(icon) {
      this.$bvModal.hide('iconSelector')
      this.item.icon = icon
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          if (this.item.id) {
            useJwt.axiosIns.patch('library', this.item)
              .then(response => {
                if (response.data) {
                  this.$emit('submit')
                  this.$root.$emit('refresh_library')
                }
              })
              .catch(e => {
                console.log(e)
              })
          } else {
            useJwt.axiosIns.post('library', this.item)
              .then(response => {
                if (response.data) {
                  this.$emit('submit')
                  this.$root.$emit('refresh_library')
                }
              })
              .catch(e => {
                console.log(e)
              })
          }
        }
      })
    },
  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-select.scss';
</style>
